import type { OrganisationDocument } from '@myplayer/schema';
import { IconBrandShield, IconCheck, IconEdit } from '@myplayer/ui';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import * as ui from './components/ui/primitives.js';
import { getFirebaseClients } from './firebase.js';
import {
  formatOrganisationName,
  mapSnapshotDocToOrganisation,
  updateOrganisationInFirestore,
  validateOrganisationDraft,
  type UpdateOrganisationInFirestoreParams,
} from './organisation-firestore.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { formatPlainAdminError } from './strings.js';
import { slugifyWorkspaceName } from './workspaces-firestore.js';

export {
  formatOrganisationName,
  mapSnapshotDocToOrganisation,
  updateOrganisationInFirestore,
  validateOrganisationDraft,
  type UpdateOrganisationInFirestoreParams,
};

export interface OrganisationProfileSectionProps {
  readonly initialOrganisationDoc?: Partial<OrganisationDocument>;
  readonly onSaveOrganisationProfile?: (params: {
    readonly orgId: string;
    readonly name: string;
    readonly slug: string;
    readonly gcpCompanyPrefixes: readonly string[];
    readonly verifiedByGs1Enabled: boolean;
    readonly tier?: 'STANDARD' | 'ENTERPRISE';
    readonly status?: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  }) => Promise<OrganisationDocument | void>;
}

export function OrganisationProfileSection({
  initialOrganisationDoc,
  onSaveOrganisationProfile,
}: OrganisationProfileSectionProps): ReactElement {
  const { session, can, organisation: contextOrg, setOrganisation } = useAuth();
  const orgId = session?.orgId ?? 'org_default';
  const canUpdateOrg = can('organisation:update');
  const isSuperAdmin = session?.personaRole === 'SUPER_ADMIN' || session?.role === 'SUPER_ADMIN';
  const [orgDoc, setOrgDoc] = useState<OrganisationDocument>(() =>
    mapSnapshotDocToOrganisation(orgId, initialOrganisationDoc ?? contextOrg ?? undefined, orgId),
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [draftName, setDraftName] = useState(orgDoc.name);
  const [draftSlug, setDraftSlug] = useState(orgDoc.slug);
  const [draftPrefixesInput, setDraftPrefixesInput] = useState(
    (orgDoc.gcpCompanyPrefixes ?? []).join(', '),
  );
  const [draftVerifiedByGs1, setDraftVerifiedByGs1] = useState(
    Boolean(orgDoc.verifiedByGs1Enabled),
  );
  const [draftTier, setDraftTier] = useState<'STANDARD' | 'ENTERPRISE'>(orgDoc.tier);
  const [draftStatus, setDraftStatus] = useState<'ACTIVE' | 'SUSPENDED' | 'ARCHIVED'>(
    orgDoc.status,
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  useEffect(() => {
    if (initialOrganisationDoc !== undefined) {
      setOrgDoc(mapSnapshotDocToOrganisation(orgId, initialOrganisationDoc, orgId));
    } else if (contextOrg !== null && contextOrg !== undefined) {
      setOrgDoc(contextOrg);
    }
  }, [orgId, initialOrganisationDoc, contextOrg]);

  const openEditModal = (): void => {
    setDraftName(orgDoc.name);
    setDraftSlug(orgDoc.slug);
    setDraftPrefixesInput((orgDoc.gcpCompanyPrefixes ?? []).join(', '));
    setDraftVerifiedByGs1(Boolean(orgDoc.verifiedByGs1Enabled));
    setDraftTier(orgDoc.tier);
    setDraftStatus(orgDoc.status);
    setErrorMessage(null);
    setEditModalOpen(true);
  };

  const handleSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!session || !canUpdateOrg) return;
    setErrorMessage(null);
    setFeedbackMessage(null);
    const validation = validateOrganisationDraft({
      name: draftName,
      slug: draftSlug,
      prefixesInput: draftPrefixesInput,
    });
    if (!validation.ok) {
      setErrorMessage(validation.error);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        orgId,
        name: validation.name,
        slug: validation.slug,
        gcpCompanyPrefixes: validation.prefixes,
        verifiedByGs1Enabled: draftVerifiedByGs1,
        ...(isSuperAdmin ? { tier: draftTier, status: draftStatus } : {}),
      };
      const updated = onSaveOrganisationProfile
        ? ((await onSaveOrganisationProfile(payload)) ??
          mapSnapshotDocToOrganisation(
            orgId,
            { ...orgDoc, ...payload, updatedBy: session.uid },
            orgId,
          ))
        : await updateOrganisationInFirestore({
            db: getFirebaseClients().db,
            caller: session.callerIdentity,
            ...payload,
          });
      setOrgDoc(updated);
      setOrganisation?.(updated);
      setEditModalOpen(false);
      setFeedbackMessage(`Organisation profile "${updated.name}" saved.`);
    } catch (err: unknown) {
      setErrorMessage(
        formatPlainAdminError(err, 'We could not save your organisation profile changes.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const closeEditModal = (): void => {
    setEditModalOpen(false);
    setErrorMessage(null);
  };

  return (
    <ui.Card data-testid="organisation-profile-card">
      {submitting && <ui.LinearSpinner />}
      <ui.CardHeader>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <ui.CardIcon>
            <IconBrandShield />
          </ui.CardIcon>
          <ui.CardTitle>Organisation Profile &amp; GS1 Licensing</ui.CardTitle>
          <ui.Badge variant={orgDoc.status === 'ACTIVE' ? 'valid' : 'warning'}>
            {orgDoc.status}
          </ui.Badge>
          <ui.Tag variant="outline" size="sm">
            {orgDoc.tier}
          </ui.Tag>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canUpdateOrg ? (
            <ui.Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={submitting}
              data-testid="edit-organisation-profile-btn"
              aria-label="Edit Organisation Profile"
              onClick={openEditModal}
            >
              <IconEdit />
              <span>Edit</span>
            </ui.Button>
          ) : (
            <span
              role="status"
              data-testid="organisation-profile-readonly-notice"
              className="text-[11px] font-medium text-fg-muted"
            >
              {formatCapabilityRestrictionNotice({
                capability: 'organisation:update',
                actionSummary: 'update organisation profile and GS1 prefixes',
                callerRole: session?.personaRole,
              })}
            </span>
          )}
        </div>
      </ui.CardHeader>
      <ui.CardContent className="space-y-4">
        <ui.CardDescription>
          Organisation display name, URL slug, and licensed GS1 Company Prefixes.
        </ui.CardDescription>
        <div className="divide-y divide-subtle border-y border-subtle">
          <ui.CardRow label="Organisation name">
            <span data-testid="organisation-profile-name" className="text-[12px] font-semibold">
              {orgDoc.name}
            </span>
          </ui.CardRow>
          <ui.CardRow label="Organisation ID &amp; slug">
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <ui.Tag variant="mono" size="sm">
                {orgDoc.orgId}
              </ui.Tag>
              <ui.Tag variant="outline" size="sm">
                /{orgDoc.slug}
              </ui.Tag>
            </div>
          </ui.CardRow>
          <ui.CardRow label="Plan tier &amp; status">
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <ui.Tag variant="outline" size="sm">
                {orgDoc.tier === 'ENTERPRISE' ? 'Enterprise Plan' : 'Standard Plan'}
              </ui.Tag>
              <ui.Badge variant={orgDoc.status === 'ACTIVE' ? 'valid' : 'warning'}>
                {orgDoc.status}
              </ui.Badge>
            </div>
          </ui.CardRow>
          <ui.CardRow label="Licensed GS1 company prefixes">
            {(orgDoc.gcpCompanyPrefixes ?? []).length > 0 ? (
              <div className="flex flex-wrap justify-end gap-1.5">
                {(orgDoc.gcpCompanyPrefixes ?? []).map((p) => (
                  <ui.Tag key={p} variant="mono" size="sm">
                    {p}
                  </ui.Tag>
                ))}
              </div>
            ) : (
              <span className="text-[12px] text-fg-muted">None set</span>
            )}
          </ui.CardRow>
          <ui.CardRow label="Verified by GS1 registry check">
            <ui.Badge variant={orgDoc.verifiedByGs1Enabled ? 'valid' : 'outline'}>
              {orgDoc.verifiedByGs1Enabled ? 'Enabled' : 'Disabled'}
            </ui.Badge>
          </ui.CardRow>
        </div>
        <ui.ToastNotification
          message={feedbackMessage}
          onDismiss={() => setFeedbackMessage(null)}
        />
      </ui.CardContent>

      <ui.ModalDialog
        open={editModalOpen}
        title="Edit Organisation Profile"
        subtitle={`Update organisation identity and GS1 licensing for ${orgDoc.orgId}`}
        icon={<IconBrandShield />}
        presentation="center"
        maxWidthClassName="max-w-2xl"
        submitting={submitting}
        onClose={closeEditModal}
        data-testid="organisation-settings-modal"
        footer={
          <>
            <ui.Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={submitting}
              onClick={closeEditModal}
            >
              Cancel
            </ui.Button>
            <ui.Button
              type="submit"
              form="organisation-settings-form"
              variant="primary"
              size="sm"
              disabled={submitting}
              aria-label="Save Organisation Profile"
            >
              <IconCheck />
              <span>Save</span>
            </ui.Button>
          </>
        }
      >
        <form
          id="organisation-settings-form"
          noValidate
          onSubmit={(e) => void handleSave(e)}
          className="space-y-4"
        >
          <div className="grid items-start gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label
                htmlFor="org-profile-name"
                className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
              >
                Organisation Name
              </label>
              <ui.Input
                id="org-profile-name"
                type="text"
                value={draftName}
                onChange={(e) => {
                  const next = e.target.value;
                  setDraftName(next);
                  if (draftSlug === slugifyWorkspaceName(draftName)) {
                    setDraftSlug(slugifyWorkspaceName(next));
                  }
                }}
                disabled={!canUpdateOrg || submitting}
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="org-profile-slug"
                className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
              >
                URL Slug
              </label>
              <ui.Input
                id="org-profile-slug"
                type="text"
                value={draftSlug}
                onChange={(e) => setDraftSlug(e.target.value)}
                disabled={!canUpdateOrg || submitting}
                className="font-mono"
              />
            </div>
          </div>

          <div className="space-y-4 border-t border-subtle pt-4">
            <div className="flex flex-col">
              <label
                htmlFor="org-gs1-prefixes"
                className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
              >
                Organisation GS1 Company Prefixes
              </label>
              <ui.Input
                id="org-gs1-prefixes"
                aria-label="Organisation GS1 Company Prefixes"
                type="text"
                value={draftPrefixesInput}
                onChange={(e) => setDraftPrefixesInput(e.target.value)}
                placeholder="9506000, 5012345"
                disabled={!canUpdateOrg || submitting}
                className="tabular-nums"
              />
              <p className="mt-1.5 text-[11px] leading-snug text-fg-secondary">
                Comma-separated 4 to 12 digit GS1 Company Prefixes licensed to your organisation.
              </p>
            </div>
            <ui.Switch
              id="org-verified-by-gs1"
              checked={draftVerifiedByGs1}
              onChange={(e) => setDraftVerifiedByGs1(e.target.checked)}
              disabled={!canUpdateOrg || submitting}
              label="Enable Verified by GS1 registry verification across workspaces"
            />
          </div>

          {isSuperAdmin && (
            <div
              data-testid="organisation-superadmin-fields"
              className="grid items-start gap-4 border-t border-subtle pt-4 md:grid-cols-2"
            >
              <div className="flex flex-col">
                <label
                  htmlFor="org-profile-tier"
                  className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
                >
                  Plan Tier (Super Admin)
                </label>
                <ui.Select
                  id="org-profile-tier"
                  value={draftTier}
                  onChange={(e) => setDraftTier(e.target.value as 'STANDARD' | 'ENTERPRISE')}
                  disabled={submitting}
                >
                  <option value="ENTERPRISE">ENTERPRISE</option>
                  <option value="STANDARD">STANDARD</option>
                </ui.Select>
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="org-profile-status"
                  className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
                >
                  Organisation Status (Super Admin)
                </label>
                <ui.Select
                  id="org-profile-status"
                  value={draftStatus}
                  onChange={(e) =>
                    setDraftStatus(e.target.value as 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED')
                  }
                  disabled={submitting}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="SUSPENDED">SUSPENDED</option>
                  <option value="ARCHIVED">ARCHIVED</option>
                </ui.Select>
              </div>
            </div>
          )}

          {errorMessage !== null && (
            <p
              role="alert"
              data-testid="organisation-settings-error"
              className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
            >
              {errorMessage}
            </p>
          )}
        </form>
      </ui.ModalDialog>
    </ui.Card>
  );
}
